<template>
  <div class="relative min-h-screen f-col">
    <vf-button
      v-if="mainContent"
      class="absolute left-0 top-0 z-toast ml-4"
      data-test-id="skip-to-main-content"
      size="tiny"
      :style="{ 'margin-top': showSkipLink ? '0.25rem' : '-100%' }"
      to="#main-content"
      variant="primary-inverse"
      @blur="showSkipLink = false"
      @click="mainContent.focus()"
      @focus="showSkipLink = true"
    >
      {{ $t.skipToMainContent }}
    </vf-button>

    <suspense v-if="!hideHeader">
      <vf-header-provider :hidden="!!route.meta.hideLayout" />
    </suspense>

    <div
      ref="mainContent"
      id="main-content"
      class="relative h-0 w-0"
      :contenteditable="!!isIOS"
      style="top: -5rem;"
      tabindex="-1"
    />

    <slot />

    <base-lazy-hydrate
      v-if="!route.meta.hideLayout && !route.meta.hideFooter && !hideFooterBasedOnRoute"
      when="visible"
    >
      <vf-footer
        class="mt-a"
        :hide-newsletter="!$feature.newsletterFormDynamic && (auth.loggedIn || auth.pending)"
      />
    </base-lazy-hydrate>

    <!--  Sierra Chat Button  -->
    <vf-button
      v-if="$chat.available && $chat.launcher"
      v-style:mb="{
        sm: `calc(var(--chat-button-margin-bottom-base, 3.75rem) + (${popUpBounds.height.value}px))`,
        md: 'var(--chat-button-margin-bottom-base, 6.25rem)',
        lg: 'var(--chat-button-margin-bottom-base, 3.75rem)',
      }"
      :aria-label="$t.chatCta.chat"
      class="fixed bottom-0 right-0 z-10 mr-5 flex center b b-white rounded-full "
      data-sierra-launcher
      style="width: 3.125rem; height: 3.125rem; box-shadow: 0 0.375rem 0.5rem 0 rgba(0, 0, 0, 0.20)"
      @click="$chat.open()"
    >
      <vf-icon class="c-white" name="chat-sierra" size="md" />
    </vf-button>
  </div>
</template>

<script lang="ts" setup>
import { isIOS } from '@vueuse/core'

const { $feature, $viewport } = useNuxtApp()
const route = useRoute()
const loyalty = useLoyaltyStore()
const toast = useToaster()
const auth = useAuthStore()
const config = useAppConfig()
const { popUpBounds } = usePopUp()
const loyaltyConfig = config.components.vf.loyalty
const { getLoyaltyMessages, isLoyaltyToastNeeded } = useLoyalty()
const mainContent = ref<HTMLDivElement>()
const showSkipLink = ref(false)
const hideHeader = computed(() => (config.pages.plp.useCustomHeader && route.path.includes('/c/')))
const isLoyaltyToastAlreadyDisplayed = useLocalStorage('isLoyaltyToastAlreadyDisplayed', false)

const hideFooterBasedOnRoute = computed(() =>
  $feature.disableFooterOnRoutes.some((path) => convertPatternToRegex(path).test(route.path)))

const displayLoyaltyToast = () => {
  if (!$feature.showLoyaltyRewardsInUtilityNav || !isLoyaltyToastNeeded() || $viewport.lg) return

  const { rewardsGreeting, title, text } = getLoyaltyMessages()

  toast.add({
    props: {
      type: '',
      variant: 'loyalty',
      classToast: loyaltyConfig.brandClasses.rewardsPreviewTilePopoverContent,
      classMessage: loyaltyConfig.brandClasses.rewardsPreviewTilePopoverContentInner,
      title: rewardsGreeting,
      message: title,
      bottomText: text,
      icon: { src: loyaltyConfig.logo, size: 'xl' }
    }
  })
  isLoyaltyToastAlreadyDisplayed.value = true
}

watch (() => loyalty.rewardsAvailable, () => {
  if (loyalty.rewardsAvailable?.length && !isLoyaltyToastAlreadyDisplayed.value) displayLoyaltyToast()
})
</script>
